import { chipifyFormula } from './extractMetrics'
import { sanitize } from './sanitize'
import { tokenToPlain, tokenize } from './tokenize'
import { validate } from './validate'

const PANDAS_TAIL = /\.(?:replace|fillna|astype|round|clip|where|map|apply)\s*\([^()]*\)\s*$/iu
const IDENTIFIER_RE = /[A-Za-z_$][A-Za-z0-9_.$-]*/gu

export type DataFrameFormulaResult =
  | { ok: true; formula: string }
  | { ok: false; source: string; reason: 'unknown-metric' | 'unsupported-wrapper' | 'invalid' }

function isOuterWrapped(value: string): boolean {
  if (!value.startsWith('(') || !value.endsWith(')')) return false
  let depth = 0
  for (let index = 0; index < value.length; index += 1) {
    if (value[index] === '(') depth += 1
    else if (value[index] === ')') depth -= 1
    if (depth < 0 || (depth === 0 && index < value.length - 1)) return false
  }
  return depth === 0
}

function stripOuterParens(value: string): string {
  let result = value.trim()
  while (isOuterWrapped(result)) result = result.slice(1, -1).trim()
  return result
}

/** Convert persisted pandas expressions without ever truncating an unrecognised wrapper. */
export function fromDataFrame(
  expression: string,
  allowedMetrics: readonly string[],
): DataFrameFormulaResult {
  const source = String(expression ?? '').trim()
  if (!source) return { ok: true, formula: '' }
  const allowed = new Set(allowedMetrics)
  let unknownMetric = false
  let value = source
    .replace(/\bdf\s*\[\s*(['"])([^'"]+)\1\s*\]/gu, (_match, _quote, field: string) => {
      const normalized = field.trim()
      if (!allowed.has(normalized)) unknownMetric = true
      return `[[m:${normalized}]]`
    })
    .replace(/\bdf\.([A-Za-z_][A-Za-z0-9_]*)\b/gu, (_match, field: string) => {
      if (!allowed.has(field)) unknownMetric = true
      return `[[m:${field}]]`
    })
  if (unknownMetric) return { ok: false, source, reason: 'unknown-metric' }

  while (PANDAS_TAIL.test(value)) value = value.replace(PANDAS_TAIL, '').trim()
  if (/\.[A-Za-z_][A-Za-z0-9_]*\s*\(/u.test(value)) {
    return { ok: false, source, reason: 'unsupported-wrapper' }
  }

  value = value.replace(/\bnp\.(?:inf|nan)\b/giu, '0').replace(/\bInfinity\b/gu, '0')
  value = chipifyFormula(value, allowedMetrics)

  const identifiers = tokenToPlain(value).match(IDENTIFIER_RE) ?? []
  if (identifiers.some((identifier) => !allowed.has(identifier))) {
    return { ok: false, source, reason: 'unknown-metric' }
  }

  const formula = sanitize(value)
  if (validate(tokenize(formula))) return { ok: false, source, reason: 'invalid' }
  return { ok: true, formula: stripOuterParens(formula) }
}
